import React, { useEffect, useState } from 'react';
import { useAppContext } from 'context/AppContext';
import { makeStyles } from 'styles/util';
import { useForm } from 'react-final-form';
import { useDispatch } from 'react-redux';
import strings from 'localization/strings';
import { convertToNumber, formatYesNo, formatPercentage } from 'helpers/StringHelper';
import { getAvailablePaymentProviders } from 'logic/paymentLogic';
import paymentProviders from 'enums/paymentProviders';
import { canonicalizeStorageSiteContacts } from 'logic/editStorageSiteLogic';
import lockTypes from 'enums/lockTypes';
import { formatShortDate } from 'helpers/DateHelper';

import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Number from 'common/Number';

const useStyles = makeStyles(({ theme }) => ({
    marginBottom: {
        margin: theme.spacing(0, 0, 4, 0)
    },
    block: {
        display: 'block'
    }
}));

const DisplayStep5 = () => {
    const { appContext } = useAppContext();
    const classes = useStyles();
    const formValues = useForm().getState().values;
    const dispatch = useDispatch();
    const values = formValues.steps[5];

    const [lockConfigurationText, setLockConfigurationText] = useState(undefined);

    useEffect(() => {
        let mounted = true;
        const func = lockTypes[values.lockType]?.formatStorageSiteLockConfiguration;
        if(func) {
            func(
                values.lockConfigurations[values.lockType],
                dispatch,
                text => {
                    if(mounted) {
                        setLockConfigurationText(text);
                    }
                }
            );
        }
        return () => {
            mounted = false;
        };
    }, [values.lockType]);

    const hasSynchronizableLockSystem = () => {
        return values.lockSystemUsers &&
               values.lockType &&
               (lockTypes[values.lockType]?.isSynchronizable ?? false);
    };

    const formatLockSystemUser = user => [
            user.name,
            user.description,
            user.lockSystemUserIdentifier,
            user.startDate || user.endDate
                ? `${formatShortDate(user.startDate, appContext) ?? ''} - ${formatShortDate(user.endDate, appContext) ?? ''}`.trim()
                : undefined
        ]
        .filter(o => o)
        .join(', ');

    return (
        <Box>
            {
                convertToNumber(values.commissionRate) !== undefined &&
                (
                    <>
                        <Typography variant="h6" gutterBottom>
                            {strings.commissionRate}
                        </Typography>

                        <Typography variant="body1" className={classes.marginBottom}>
                            {formatPercentage(convertToNumber(values.commissionRate) / 100, appContext)}
                        </Typography>
                    </>
                )
            }
            <Typography variant="h6" gutterBottom>
                {strings.storageSiteIsSearchable}
            </Typography>

            <Typography variant="body1" className={classes.marginBottom}>
                {formatYesNo(values.searchable)}
            </Typography>

            <Typography variant="h6" gutterBottom>
                {strings.searchPriority}
            </Typography>

            <Typography variant="body1" className={classes.marginBottom}>
                { values.searchPriority || 0 }
            </Typography>

            <Typography variant="h6" gutterBottom>
                {strings.regionSearchPriority}
            </Typography>

            <Typography variant="body1" className={classes.marginBottom}>
                { values.regionSearchPriority || 0 }
            </Typography>

            <Typography variant="h6" gutterBottom>
                {strings.distanceSearchAdjustment}
            </Typography>

            <Typography variant="body1" className={classes.marginBottom}>
                {
                    strings.formatString(strings.xKm, <Number value={convertToNumber(values.distanceSearchAdjustment) || 0}/>)
                }
            </Typography>

            <Typography variant="h6" gutterBottom>
                {strings.paymentProvidersTitle}
            </Typography>

            {
                getAvailablePaymentProviders(appContext).length > 1 &&
                (
                    <Typography variant="body1" className={classes.marginBottom}>
                        {
                            values.paymentProviders.length === 0 && '-'
                        }
                        {
                            values.paymentProviders.length > 0 && values.paymentProviders.map(o => paymentProviders[o].shortTitle).join(', ')
                        }
                    </Typography>
                )
            }

            <Typography variant="h6" gutterBottom>
                {strings.allowImmediateBookings}
            </Typography>

            <Typography variant="body1" className={classes.marginBottom}>
                {formatYesNo(values.allowImmediateBookings)}
            </Typography>

            <Typography variant="h6" gutterBottom>
                {strings.considerBookingsAsServices}
            </Typography>

            <Typography variant="body1" className={classes.marginBottom}>
                {formatYesNo(values.considerBookingsAsServices)}
            </Typography>

            <Typography variant="h6" gutterBottom>
                {strings.lockType}
            </Typography>

            <Typography variant="body1" className={classes.marginBottom}>
                {
                    values.lockType === 'none'
                        ? strings.lockTypes.none.title
                        : <>{lockTypes[values.lockType].title}: {lockConfigurationText}</>
                }
            </Typography>

            {
                hasSynchronizableLockSystem() &&
                (
                    <>
                        <Typography variant="h6" gutterBottom>
                            {strings.lockSystemUsers}
                        </Typography>
                        {
                            values.lockSystemUsers.length === 0 &&
                            (
                                <Typography variant="body1" className={classes.marginBottom}>
                                    {strings.noLockSystemUsers}
                                </Typography>
                            )
                        }
                        {
                            values.lockSystemUsers.length > 0 &&
                            (
                                <ul>
                                    {
                                        values.lockSystemUsers.map((user, index) => (
                                            <li key={index}>{formatLockSystemUser(user)}</li>
                                        ))
                                    }
                                </ul>
                            )
                        }
                    </>
                )
            }

            <Typography variant="h6" gutterBottom>
                {strings.storageSiteContacts}
            </Typography>

            <Typography variant="body1" className={classes.marginBottom}>
                {
                    Object.keys(values.contacts)
                        .map(type => <Box key={type} component="span" className={classes.block}>{strings.contactTypes[type]}: {canonicalizeStorageSiteContacts(values.contacts[type]) ?? '-'}</Box>)
                }
            </Typography>
        </Box>
    );
};

export default DisplayStep5;
